import { api, extractErrorMessage, type ApiEnvelope } from './api';
import type { AuthRegistration, AuthUser } from './auth-store';
import type { CertStatusCode } from './store';

function unwrap<T>(env: ApiEnvelope<T>): T {
    if (env.status !== 'ok' || env.data === undefined) {
        throw new Error(env.message ?? 'API error');
    }
    return env.data;
}

/** Wrap axios call — kalau fail (HTTP non-2xx), extract pesan real dari response body. */
async function safeCall<T>(fn: () => Promise<T>): Promise<T> {
    try {
        return await fn();
    } catch (e) {
        throw new Error(extractErrorMessage(e));
    }
}

// ─── Auth ─────────────────────────────────────────────────────────

export async function login(identifier: string, password: string): Promise<AuthUser> {
    return safeCall(async () => {
        const res = await api.post<ApiEnvelope<{ user: AuthUser }>>('/api/auth/login', {
            identifier,
            password,
        });
        return unwrap(res.data).user;
    });
}

export async function logout(): Promise<void> {
    await safeCall(() => api.post('/api/auth/logout'));
}

export type ActivatePayload = {
    email: string;
    phone: string;
    nik: string;
    fullName: string;
    password: string;
    password_confirmation: string;
};

export async function activateAccount(payload: ActivatePayload): Promise<AuthUser> {
    return safeCall(async () => {
        const res = await api.post<ApiEnvelope<{ user: AuthUser }>>(
            '/api/auth/activate',
            payload
        );
        return unwrap(res.data).user;
    });
}

export async function me(): Promise<{ user: AuthUser; registration: AuthRegistration | null }> {
    return safeCall(async () => {
        const res = await api.get<
            ApiEnvelope<{ user: AuthUser; registration: AuthRegistration | null }>
        >('/api/auth/me');
        return unwrap(res.data);
    });
}

// ─── User Portal ──────────────────────────────────────────────────

export type UserProfile = {
    user: {
        id: number;
        code: string;
        fullname: string;
        dateBirth: string | null;
        placeBirth: string | null;
        gender: string | null;
        email: string | null;
        phone: string | null;
    };
    registration: {
        id: number;
        nik: string | null;
        fullName: string | null;
        address: string | null;
        city: string | null;
        province: string | null;
        statusCode: CertStatusCode;
        lastResultCode: string | null;
        lastResultDesc: string | null;
        ktpPhotoUrl: string | null;
        npwpPhotoUrl: string | null;
        certExpiredAt: string | null;
    } | null;
    specimen: {
        id: number;
        specimenId: string | null;
        fileName: string | null;
        imageUrl: string | null;
        updatedAt: string;
    } | null;
};

export async function getProfile(): Promise<UserProfile> {
    return safeCall(async () => {
        const res = await api.get<ApiEnvelope<UserProfile>>('/api/user/profile');
        return unwrap(res.data);
    });
}

export async function updateProfile(data: {
    phone?: string;
    address?: string;
    city?: string;
    province?: string;
}): Promise<UserProfile> {
    return safeCall(async () => {
        const res = await api.put<ApiEnvelope<UserProfile>>('/api/user/profile', data);
        return unwrap(res.data);
    });
}

export type CertificateStatus = {
    statusCode: CertStatusCode;
    resultCode: string;
    email: string;
    phone: string | null;
    isExpired: string | null;
    certExpiredAt: string | null;
    terminal: boolean;
};

export async function getCertificate(): Promise<CertificateStatus> {
    return safeCall(async () => {
        const res = await api.get<ApiEnvelope<CertificateStatus>>('/api/user/certificate');
        return unwrap(res.data);
    });
}

export async function updateSpecimen(pngBlob: Blob): Promise<{
    specimenId: string | null;
    imageUrl: string | null;
}> {
    return safeCall(async () => {
        const fd = new FormData();
        fd.append('signature', pngBlob, 'signature.png');
        const res = await api.post<
            ApiEnvelope<{ specimenId: string | null; imageUrl: string | null }>
        >('/api/user/specimen', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return unwrap(res.data);
    });
}

export type ActivityLog = {
    id: number;
    code: string;
    description: string | null;
    ip: string | null;
    success: boolean;
    at: string;
};

export async function getActivity(): Promise<ActivityLog[]> {
    return safeCall(async () => {
        const res = await api.get<ApiEnvelope<ActivityLog[]>>('/api/user/activity');
        return unwrap(res.data);
    });
}
